import { useEffect, useState } from 'react';
import Navbar from 'react-bootstrap/Navbar';
import Nav from 'react-bootstrap/Nav';
import Container from 'react-bootstrap/Container';
import Button from 'react-bootstrap/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faRightFromBracket } from '@fortawesome/free-solid-svg-icons';
import UniversityPicker from './UniversityPicker';
import CourseOverview from './CourseOverview';
import FullRoster from './FullRoster';
import Outreach from './Outreach';
import Footer from './Footer';

const APP_VERSION = __APP_VERSION__;

const UNIVERSITY_STORAGE_KEY = 'howdy.selectedUniversity';

const TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'full-roster', label: 'Full Roster' },
  { key: 'outreach', label: 'Outreach' },
];

const BRAND_HEIGHT = 40;

function BrandLogo({ logoUrl, fallbackText, ready }) {
  const [imgState, setImgState] = useState('loading');

  useEffect(() => {
    setImgState(logoUrl ? 'loading' : 'error');
  }, [logoUrl]);

  const containerStyle = {
    height: BRAND_HEIGHT,
    display: 'flex',
    alignItems: 'center',
    minWidth: 0,
    opacity: ready ? 1 : 0,
    transition: 'opacity 200ms ease',
  };

  const useImage = ready && logoUrl && imgState !== 'error';

  return (
    <span style={containerStyle}>
      {useImage && (
        <img
          src={logoUrl}
          alt={fallbackText || ''}
          onLoad={() => setImgState('loaded')}
          onError={() => setImgState('error')}
          style={{
            height: `${BRAND_HEIGHT}px`,
            width: 'auto',
            maxWidth: '220px',
            objectFit: 'contain',
            display: 'block',
            opacity: imgState === 'loaded' ? 1 : 0,
            transition: 'opacity 200ms ease',
          }}
        />
      )}
      {ready && !useImage && (
        <span className="text-truncate">{fallbackText}</span>
      )}
    </span>
  );
}

function hexToRgb(hex) {
  if (!hex) return null;
  const m = hex.replace('#', '').match(/^([0-9a-f]{6}|[0-9a-f]{3})$/i);
  if (!m) return null;
  let h = m[1];
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  const n = parseInt(h, 16);
  return `${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}`;
}

export default function App() {
  const [me, setMe] = useState(null);
  const [university, setUniversity] = useState(null);
  const [branding, setBranding] = useState(null);
  const [brandingLoaded, setBrandingLoaded] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [rosterFilter, setRosterFilter] = useState(null);

  const navigateToRoster = (filter) => {
    setRosterFilter({ ...filter, nonce: (rosterFilter?.nonce ?? 0) + 1 });
    setActiveTab('full-roster');
  };

  useEffect(() => {
    let cancelled = false;
    fetch('/api/me')
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (cancelled || !data) return;
        setMe(data);
        if (data.role === 'partner' && data.university_id) {
          setUniversity({ university_id: data.university_id, university_name: '' });
          return;
        }
        try {
          const raw = localStorage.getItem(UNIVERSITY_STORAGE_KEY);
          if (raw) setUniversity(JSON.parse(raw));
        } catch {
          // ignore storage errors
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const isPartner = me?.role === 'partner';

  useEffect(() => {
    try {
      if (university) {
        localStorage.setItem(UNIVERSITY_STORAGE_KEY, JSON.stringify(university));
      } else {
        localStorage.removeItem(UNIVERSITY_STORAGE_KEY);
      }
    } catch {
      // ignore storage errors
    }
  }, [university]);

  useEffect(() => {
    if (!university?.university_id) {
      setBranding(null);
      setBrandingLoaded(false);
      return;
    }
    const url = `/api/university/${encodeURIComponent(university.university_id)}`;
    let cancelled = false;
    setBrandingLoaded(false);
    fetch(url, { headers: { Accept: 'application/vnd.howdy.university+json' } })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (cancelled) return;
        setBranding(data);
        setBrandingLoaded(true);
      })
      .catch(() => {
        if (cancelled) return;
        setBranding(null);
        setBrandingLoaded(true);
      });
    return () => { cancelled = true; };
  }, [university?.university_id]);

  useEffect(() => {
    const root = document.documentElement;
    const primary = branding?.branding_primary_color;
    const secondary = branding?.branding_secondary_color;
    const primaryRgb = hexToRgb(primary);
    const secondaryRgb = hexToRgb(secondary);
    if (primary && primaryRgb) {
      root.style.setProperty('--bs-primary', primary);
      root.style.setProperty('--bs-primary-rgb', primaryRgb);
      root.style.setProperty('--bs-primary-text-emphasis', primary);
      root.style.setProperty('--bs-primary-bg-subtle', `rgba(${primaryRgb}, 0.10)`);
      root.style.setProperty('--bs-primary-border-subtle', `rgba(${primaryRgb}, 0.35)`);
      root.style.setProperty('--bs-link-color', primary);
      root.style.setProperty('--bs-link-color-rgb', primaryRgb);
      root.style.setProperty('--bs-link-hover-color', primary);
      root.style.setProperty('--bs-nav-underline-link-active-color', primary);
      root.style.setProperty('--bs-nav-underline-border-color', primary);
      root.style.setProperty('--brand-primary', primary);
    }
    if (secondary && secondaryRgb) {
      root.style.setProperty('--brand-secondary', secondary);
      root.style.setProperty('--brand-secondary-rgb', secondaryRgb);
    }
    return () => {
      root.style.removeProperty('--bs-primary');
      root.style.removeProperty('--bs-primary-rgb');
      root.style.removeProperty('--bs-primary-text-emphasis');
      root.style.removeProperty('--bs-primary-bg-subtle');
      root.style.removeProperty('--bs-primary-border-subtle');
      root.style.removeProperty('--bs-link-color');
      root.style.removeProperty('--bs-link-color-rgb');
      root.style.removeProperty('--bs-link-hover-color');
      root.style.removeProperty('--bs-nav-underline-link-active-color');
      root.style.removeProperty('--bs-nav-underline-border-color');
      root.style.removeProperty('--brand-primary');
      root.style.removeProperty('--brand-secondary');
      root.style.removeProperty('--brand-secondary-rgb');
    };
  }, [branding]);

  const handleChangeUniversity = () => {
    setUniversity(null);
    setBranding(null);
    setActiveTab('overview');
  };

  const logoUrl = branding?.logo_url;

  if (!me) {
    return null;
  }

  if (!university) {
    return (
      <>
        <UniversityPicker onSelect={setUniversity} />
        <Footer version={APP_VERSION} />
      </>
    );
  }

  let view;
  switch (activeTab) {
    case 'full-roster':
      view = <FullRoster university={university} pendingFilter={rosterFilter} />;
      break;
    case 'outreach':
      view = <Outreach university={university} />;
      break;
    case 'overview':
    default:
      view = <CourseOverview university={university} onNavigateToRoster={navigateToRoster} />;
      break;
  }

  return (
    <>
      <Navbar
        bg="white"
        expand="xl"
        className="border-bottom shadow-sm"
      >
        <Container fluid="xl">
          <Navbar.Brand
            className="fw-bold d-flex align-items-center"
            style={{ maxWidth: '60vw', minWidth: 0 }}
            aria-label={university.university_name}
          >
            <BrandLogo
              ready={brandingLoaded}
              logoUrl={logoUrl}
              fallbackText={branding?.abbreviation || university.university_name}
            />
          </Navbar.Brand>
          <Navbar.Toggle aria-controls="howdy-navbar" />
          <Navbar.Collapse id="howdy-navbar">
            <Nav
              variant="underline"
              activeKey={activeTab}
              onSelect={(k) => k && setActiveTab(k)}
              className="me-auto flex-xl-row"
            >
              {TABS.map((tab) => (
                <Nav.Item key={tab.key}>
                  <Nav.Link
                    eventKey={tab.key}
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    {tab.label}
                  </Nav.Link>
                </Nav.Item>
              ))}
            </Nav>
            <div className="d-flex flex-column flex-xl-row gap-2 mt-3 mt-xl-0">
              {!isPartner && (
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={handleChangeUniversity}
                  style={{ whiteSpace: 'nowrap' }}
                >
                  <FontAwesomeIcon icon={faArrowLeft} className="me-1" />Change university
                </Button>
              )}
              <Button
                variant="outline-secondary"
                size="sm"
                href="/logout"
                style={{ whiteSpace: 'nowrap' }}
              >
                <FontAwesomeIcon icon={faRightFromBracket} className="me-1" />Sign out
              </Button>
            </div>
          </Navbar.Collapse>
        </Container>
      </Navbar>
      {view}
      <Footer version={APP_VERSION} />
    </>
  );
}
